import React, { useState } from 'react';
import axios from '../utils/axiosConfig';
import { toast } from 'react-toastify';
import { IysIzinDetay } from './IysKayitListesi';
import { useTranslation } from 'react-i18next';

interface IysKilitliAlanProps {
    /** Aktif (AktifMi=true, IzinDurumu=ONAY) IYS kaydı. Yoksa null. */
    aktifIzin: IysIzinDetay | null;
    cariID: number;
    takipNo: string;
    ekleyenID: number;
    iletisimTipiLabel: string; // 'Telefon 1', 'Telefon 2', 'E-Posta'
    onUnlocked: () => void;
    /** Render prop: input bileşenini döndürür. readOnly true ise kilitlemiş demek. */
    children: (readOnly: boolean) => React.ReactNode;
    /** true ise IYS kilitleme devre dışı — alan direk düzenlenebilir görünür */
    disabled?: boolean;
}

const IysKilitliAlan: React.FC<IysKilitliAlanProps> = ({
    aktifIzin, cariID, takipNo, ekleyenID, iletisimTipiLabel, onUnlocked, children, disabled
}) => {
    const { t } = useTranslation();
    const [showConfirm, setShowConfirm] = useState(false);
    const [loading, setLoading] = useState(false);
    const [unlocked, setUnlocked] = useState(false);

    // IYS özelliği pasifse kilitleme yok
    if (disabled) return <>{children(false)}</>;

    const isLocked = !!aktifIzin && !unlocked;

    const handleUnlockConfirm = async () => {
        if (!aktifIzin) return;
        setLoading(true);
        try {
            // 1) IYS kaydını pasife al
            await axios.put(`/api/CariIysIzinleri/${aktifIzin.iysIzinID}/pasife-al`);

            // 2) İşlem notuna kaydet
            const aciklama = `IYS İzni Pasife Alındı: ${iletisimTipiLabel} (${aktifIzin.iletisimVerisi}) alanı değiştirilmek üzere IYS onayı pasife alındı. Yeniden izin alınması gerekiyor.`;
            await axios.post('/api/Notlar', {
                aciklama,
                ekleyenID,
                aktifMi: true,
                takipNo
            });

            setUnlocked(true);
            setShowConfirm(false);
            toast.warn(`${iletisimTipiLabel} IYS izni pasife alındı. Alan artık düzenlenebilir.`);
            onUnlocked();
        } catch (err: any) {
            toast.error(err?.response?.data?.message  || t('msg.islemHatasi'));
        } finally {
            setLoading(false);
        }
    };

    return (
        <div style={{ position: 'relative' }}>
            {children(!unlocked && !!aktifIzin)}

            {/* Kilit rozeti - aktif IYS varsa göster */}
            {isLocked && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 4 }}>
                    <span style={{
                        display: 'inline-flex', alignItems: 'center', gap: 4,
                        background: '#f0fdf4', border: '1px solid #86efac',
                        borderRadius: 4, padding: '2px 8px', fontSize: 11, color: '#166534'
                    }}>
                        <i className="fas fa-lock" style={{ fontSize: 10 }}></i>
                        IYS Onaylı
                    </span>
                    <button
                        type="button"
                        onClick={() => setShowConfirm(true)}
                        style={{
                            background: 'none', border: '1px solid #d97706', color: '#d97706',
                            borderRadius: 4, padding: '2px 8px', cursor: 'pointer', fontSize: 11,
                            display: 'inline-flex', alignItems: 'center', gap: 4
                        }}>
                        <i className="fas fa-edit" style={{ fontSize: 10 }}></i>Değiştir
                    </button>
                </div>
            )}

            {/* Onay Modalı */}
            {showConfirm && (
                <div style={{
                    position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)',
                    zIndex: 1300, display: 'flex', alignItems: 'center', justifyContent: 'center'
                }} onClick={() => !loading && setShowConfirm(false)}>
                    <div style={{
                        background: '#fff', borderRadius: 8, width: 420, maxWidth: '92vw',
                        padding: 24, boxShadow: '0 4px 24px rgba(0,0,0,0.2)'
                    }} onClick={e => e.stopPropagation()}>

                        {/* İkon + Başlık */}
                        <div style={{ display: 'flex', gap: 14, marginBottom: 16 }}>
                            <div style={{
                                width: 40, height: 40, borderRadius: '50%',
                                background: '#fef3c7', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0
                            }}>
                                <i className="fas fa-exclamation-triangle" style={{ color: '#d97706', fontSize: 18 }}></i>
                            </div>
                            <div>
                                <div style={{ fontWeight: 700, fontSize: 15, color: '#111827', marginBottom: 6 }}>
                                    IYS İznini Pasife Al
                                </div>
                                <div style={{ fontSize: 13, color: '#6b7280', lineHeight: 1.6 }}>
                                    <b>{iletisimTipiLabel}</b> alanı için IYS izni alınmış.
                                    Bu alanı değiştirirseniz:
                                    <ul style={{ margin: '8px 0 0', paddingLeft: 18 }}>
                                        <li>Mevcut IYS onayı <b>pasife alınacak</b></li>
                                        <li>İşlem <b>not olarak kaydedilecek</b></li>
                                        <li>Değişiklikten sonra <b>yeniden IYS izni</b> almanız gerekecek</li>
                                    </ul>
                                </div>
                            </div>
                        </div>

                        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
                            <button
                                type="button"
                                disabled={loading}
                                onClick={() => setShowConfirm(false)}
                                style={{
                                    padding: '8px 18px', background: '#fff', border: '1px solid #d1d5db',
                                    borderRadius: 6, cursor: 'pointer', fontSize: 13, color: '#374151'
                                }}>
                                İptal
                            </button>
                            <button
                                type="button"
                                disabled={loading}
                                onClick={handleUnlockConfirm}
                                style={{
                                    padding: '8px 18px', background: '#d97706', border: 'none',
                                    borderRadius: 6, cursor: 'pointer', fontSize: 13, color: '#fff', fontWeight: 600,
                                    display: 'flex', alignItems: 'center', gap: 6
                                }}>
                                {loading
                                    ? <><i className="fas fa-spinner fa-spin"></i>İşleniyor...</>
                                    : <><i className="fas fa-unlock-alt"></i>Evet, Kilidi Kaldır</>
                                }
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default IysKilitliAlan;
